import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo } from "react";
import {
  generateMfCentralOtp,
  fetchMfCentralStatus,
  verifyMfCentralOtp,
  fetchCasUploadStatus,
  uploadCasApi,
  mfCentralV2RequestApi,
  mfCentralV2UploadQrApi,
} from "../api/holding";
import { MfCentralRefetchInterval, MFCentralStatusResponse } from "../types";
import { MFCENTRAL_IN_PROGESS_STATUS } from "../constants/mf-central";

export const useGenerateMfCentralOtpMutation = (
  investmentAccountId: string
) => {
  return useMutation({
    mutationKey: ["mf-central-otp", { investmentAccountId }],
    mutationFn: (contactInfo?: string) =>
      generateMfCentralOtp(investmentAccountId, contactInfo),
  });
};

export const mfCentralStatusQueryOptions = (
  investmentAccountId: string,
  enabled?: boolean,
  refetchInterval?: MfCentralRefetchInterval
) =>
  queryOptions({
    queryKey: ["mf-central-status", { investmentAccountId }],
    queryFn: () => fetchMfCentralStatus(investmentAccountId),
    enabled: !!investmentAccountId && enabled,
    refetchInterval,
    structuralSharing: false,
  });

export const useMfCentralStatus = (
  investmentAccountId: string,
  enabled?: boolean,
  refetchInterval?: MfCentralRefetchInterval
) => {
  return useQuery(
    mfCentralStatusQueryOptions(investmentAccountId, enabled, refetchInterval)
  );
};

export function useMfCentralImportState(
  importStatus?: MFCentralStatusResponse
) {
  return useMemo(() => {
    let importState: "idle" | "in-progress" | "expired" = "idle";
    if (
      importStatus &&
      MFCENTRAL_IN_PROGESS_STATUS.includes(importStatus?.status)
    ) {
      const updatedAt = importStatus?.updated_at;

      const updatedAtWithinLastMinute =
        !!updatedAt && Date.now() - new Date(updatedAt).getTime() < 60 * 1000;

      if (updatedAtWithinLastMinute) {
        importState = "in-progress";
      } else {
        importState = "expired";
      }
    }
    return importState;
  }, [importStatus]);
}

export const useVerifyMfCentralOtpMutation = (investmentAccountId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["mf-central-otp", { investmentAccountId }],
    mutationFn: ({ otp }: { otp: string }) =>
      verifyMfCentralOtp(investmentAccountId, otp),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: mfCentralStatusQueryOptions(investmentAccountId).queryKey,
      });
    },
    onError: () => {
      queryClient.invalidateQueries({
        queryKey: mfCentralStatusQueryOptions(investmentAccountId).queryKey,
      });
    },
  });
};

export function useCasUpload() {
  return useMutation({
    mutationFn: uploadCasApi,
  });
}

export function useCasUploadStatus(
  variables: { investmentAccountId: string },
  options?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ["cas-upload-status"],
    queryFn: () => fetchCasUploadStatus(variables.investmentAccountId),
    ...options,
  });
}

export function useMfCentralV2RequestMutation() {
  return useMutation({
    mutationFn: mfCentralV2RequestApi,
  });
}

export function useMfCentralV2UploadQrMutation() {
  return useMutation({
    mutationFn: mfCentralV2UploadQrApi,
  });
}
